import { BestelllistenEintrag } from "../arrangement/Stückliste";

import React, { useEffect, useState } from "react";
import { MinimalNumberInput } from "../commons/MinimalNumberInput";
import { NumberInput } from "../commons/NumberInput";
import { MinimalTextInput } from "../commons/MinimalTextInput";

type internalUDArticle = {
  Anzahl: number;
  Beschreibung: string;
  Einzelpreis?: number;
  Preis?: number;
};

const maxRows = 3;

const validRow = (r: internalUDArticle) =>
  r.Anzahl > 0 && r.Beschreibung?.length > 0 && r.Einzelpreis >= 0;
const localFromRemote = (
  remote: BestelllistenEintrag[],
  oldLocal: internalUDArticle[],
  canChange: boolean
) => {
  const newRows: typeof oldLocal = [];
  const oldCopy = [...oldLocal];
  remote.forEach((r) => {
    newRows.push({
      Anzahl: r.Anzahl,
      Beschreibung: r.ArtikeltextPreisuebersicht,
      Einzelpreis: r.Einzelpreis,
      Preis: r.Preis,
    });
    const oldIndex = oldCopy.findIndex((o) => o.Beschreibung == r.ArtikeltextPreisuebersicht);
    if (oldIndex >= 0) {
      oldCopy.splice(oldIndex, 1);
    }
  });
  if (newRows.length < maxRows && canChange) {
    newRows.push({
      Anzahl: oldCopy[0]?.Anzahl ?? 1,
      Beschreibung: oldCopy[0]?.Beschreibung ?? null,
      Einzelpreis: oldCopy[0]?.Einzelpreis ?? 0,
    });
  }
  return newRows;
};

export function UserDefinedArticleRows({
  allArticleRows = [],
}: {
  allArticleRows: BestelllistenEintrag[];
}) {
  const canChange = ["admin", "haendler"].includes(APP.settings.userRole);
  const [rows, setRows] = useState<internalUDArticle[]>(
    localFromRemote(allArticleRows, [], canChange)
  );
  useEffect(() => {
    const newRows = localFromRemote(allArticleRows, rows, canChange);
    if (!Commons.isEqualEpsilon(rows, newRows)) {
      setRows(newRows);
    }
  }, [allArticleRows, rows]);

  const updateRow = (amount: number, description: string, price: number, idx: number) => {
    const row = rows[idx];
    const newRow = { ...row, Anzahl: amount, Beschreibung: description, Einzelpreis: price };
    const isRowValid = validRow(row);
    const isNewRowValid = validRow(newRow);

    if (isRowValid) {
      //alte Daten waren gut und wurden gespeichert -> löschen
      const ind = APP.arrangement.userDefinedArticles.findIndex(
        (r) => r.description == row.Beschreibung
      );
      if (ind >= 0) {
        APP.arrangement.userDefinedArticles.splice(ind, 1);
      }
    }
    if (isNewRowValid) {
      //neue Daten sind gut -> speichern
      APP.arrangement.userDefinedArticles.push({ amount, description, price });
    }

    APP.logUserAction({ action: "changeUserDefinedRowStückliste", amount, description, price });

    //Darstellung immer aktualisieren;
    setRows((rows) => {
      rows[idx] = newRow;
      return rows;
    });
    APP.dirty();
  };

  /**
   *             <th className="align-left">{I18N("Stk.")}</th>
   *             <th className="align-left">{I18N("Art.Nr.")}</th>
   *             <th className="align-left">{I18N("Beschreibung")}</th>
   *             <th className="align-right">{I18N("Einzelpreis")}</th>
   *             <th className="align-right">{I18N("Gesamtpreis")}</th>
   */
  const { isFirst, unit } = getCurrentCurrencyFormating();
  return (
    <>
      {rows.map((row, idx) => (
        <tr key={idx} className={`partslist-group ${canChange ? "extra_articles" : ""}`}>
          <td className="align-left vertical-align-top no-wrap">
            {canChange ? (
              <MinimalNumberInput
                id={"extra_article_" + idx}
                ariaLabel={I18N("Artikel Anzahl")}
                value={row.Anzahl}
                onChange={(x) => updateRow(x, row.Beschreibung, row.Einzelpreis, idx)}
                min={0}
              />
            ) : (
              `${row.Anzahl} x`
            )}
          </td>
          <td className="align-left vertical-align-top no-wrap" colSpan={2}>
            {canChange ? (
              <MinimalTextInput
                ariaLabel={I18N("Artikel Beschreibung")}
                value={row.Beschreibung}
                setValue={(x) => updateRow(row.Anzahl, x, row.Einzelpreis, idx)}
              />
            ) : (
              row.Beschreibung
            )}
          </td>
          <td className="align-right vertical-align-center no-wrap">
            {canChange ? (
              <NumberInput
                id={"extra_article_price" + idx}
                ariaLabel={I18N("Artikel Preis")}
                value={row.Einzelpreis}
                className={"number-input"}
                setValue={(x) => updateRow(row.Anzahl, row.Beschreibung, x, idx)}
                min={0}
                decimals={2}
                unit={unit}
                unitFirst={isFirst}
                showSteps={false}
              />
            ) : (
              row.Einzelpreis != null && I18N.formatCurrency(row.Einzelpreis)
            )}
          </td>
          <td className="align-right vertical-align-center no-wrap">
            {row.Preis != null && I18N.formatCurrency(row.Preis)}
          </td>
        </tr>
      ))}
    </>
  );
}

function getCurrentCurrencyFormating() {
  const pattern = I18N.formatCurrency(0, 0);
  const unitFirst = /^(.*?)(\s*)0$/;
  const unitLast = /^0(\s*)(.*)$/;
  let unit = "€";
  let isFirst = false;
  let spacing = true;
  let match;
  if ((match = unitFirst.exec(pattern))) {
    spacing = match[2].length > 0;
    unit = match[1];
    isFirst = true;
  } else if ((match = unitLast.exec(pattern))) {
    spacing = match[1].length > 0;
    unit = match[2];
  }
  return { isFirst, unit, spacing };
}
